import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { logChatbotLoadChatbot } from '../../bi';
import { useFedopsLogger } from '../../common/providers/fedopsLogger';
import { selectConversationId } from '../../common/selectors/chat';
import { selectIsCopilotInitialized, selectLoadPageUrl } from '../../common/selectors/meta';
import { selectSelectedSite } from '../../common/selectors/sites';
import { selectWidgetSessionId } from '../../common/selectors/widgetSession';
import { loadChatbotStartChat } from '../../common/thunks/chatbotStartChat.thunk';
import { widgetSDK } from '../../sdk/widgetSDK';
import { addWixRecorder } from '../../util/addWixRecorder';
import { renderModel } from '../../util/renderModel';

const isCopilot = renderModel.isCopilot();

const useAppLoaded = () => {
  const fedopsLogger = useFedopsLogger();
  const store = useStore();

  return useCallback(() => {
    logChatbotLoadChatbot(store.getState());
    fedopsLogger.appLoaded();
    const sessionId = selectWidgetSessionId(store.getState());
    const conversationId = selectConversationId(store.getState());
    addWixRecorder({ sessionId, conversationId });
  }, [fedopsLogger, store]);
};

export const useLoadApp = () => {
  const dispatch = useDispatch();
  const appLoaded = useAppLoaded();
  const isCopilotInitialized = useSelector(selectIsCopilotInitialized);
  const loadPageUrl = useSelector(selectLoadPageUrl);
  const selectedSite = useSelector(selectSelectedSite);

  const fedopsLogger = useFedopsLogger();
  const initialEditorType = useRef(selectedSite?.editorType);
  useEffect(() => {
    if (isCopilot && !isCopilotInitialized) {
      return;
    }
    const loadApp = async () => {
      const context = await widgetSDK.getContext(isCopilot ? loadPageUrl : undefined);
      await dispatch(loadChatbotStartChat({ context, editorType: initialEditorType.current }));
      appLoaded();
    };
    fedopsLogger.appLoadStarted();
    void loadApp();
  }, [appLoaded, dispatch, fedopsLogger, isCopilotInitialized, loadPageUrl]);

  const prevEditorType = useRef<string | undefined>(selectedSite?.editorType);
  useEffect(() => {
    if (selectedSite?.editorType && selectedSite.editorType !== prevEditorType.current) {
      prevEditorType.current = selectedSite.editorType;
      const reload = async () => {
        const context = await widgetSDK.getContext(isCopilot ? loadPageUrl : undefined);
        await dispatch(loadChatbotStartChat({ context, editorType: selectedSite.editorType }));
      };
      void reload();
    }
  }, [selectedSite?.editorType, dispatch, loadPageUrl]);
};
